import { ApolloClient, ApolloLink, HttpLink, NormalizedCacheObject } from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import fetch from 'cross-fetch';
import { useMemo } from 'react';
import { cache } from './cache';
interface CreateApolloClientParams {
  token?: string;
  initialState?: NormalizedCacheObject;
}
export function createApolloClient({
  token,
  initialState
}: CreateApolloClientParams = {}): ApolloClient {
  const isSsrMode = typeof window === 'undefined';
  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GATEWAY_URL,
    fetch
  });
  const authLink = new SetContextLink(async ({
    headers
  }) => {
    // If this is SSR, DO NOT PASS THE REQUEST HEADERS.
    // Just send along the authorization headers.
    // The **correct** headers will be supplied by the `getServerSideProps` invocation of the query
    return {
      headers: {
        ...(!isSsrMode ? headers : []),
        Authorization: token != null ? `JWT ${token}` : undefined,
        'x-graphql-client-name': 'watch',
        'x-graphql-client-version': process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? ''
      }
    };
  });
  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Number.POSITIVE_INFINITY,
      jitter: true
    },
    attempts: {
      max: 5
    }
  });
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([retryLink, authLink, httpLink]),
    cache: cache().restore(initialState ?? {}),
    devtools: {
      enabled: true
    }
  });
}
export function useApolloClient({
  token,
  initialState
}: CreateApolloClientParams = {}): ApolloClient {
  return useMemo(() => createApolloClient({
    token,
    initialState
  }), [token, initialState]);
}